import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';

type StatusFilter = 'all' | 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';

export default function AdminSupportPage() {
  const { t, i18n } = useTranslation();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const pageSize = 50;

  const ticketsQuery = useQuery({
    queryKey: ['admin:support-tickets', page],
    queryFn: () => apiClient.getAdminSupportTickets(page * pageSize, pageSize),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ ticketId, status }: { ticketId: string; status: string }) =>
      apiClient.updateAdminSupportTicketStatus(ticketId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin:support-tickets'] });
      setSelectedTicket(null);
    },
    onError: () => addToast(t('admin.ticketUpdateError'), 'error'),
  });

  const tickets = ticketsQuery.data?.data || [];
  const total = ticketsQuery.data?.total || 0;

  const filteredTickets = statusFilter === 'all'
    ? tickets
    : tickets.filter((t: any) => t.status === statusFilter);

  const openCount = tickets.filter((t: any) => t.status === 'OPEN').length;
  const inProgressCount = tickets.filter((t: any) => t.status === 'IN_PROGRESS').length;
  const resolvedCount = tickets.filter((t: any) => t.status === 'RESOLVED').length;
  const closedCount = tickets.filter((t: any) => t.status === 'CLOSED').length;

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'OPEN': return 'bg-red-100 text-red-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'RESOLVED': return 'bg-green-100 text-green-800';
      case 'CLOSED': return 'bg-neutral-100 text-neutral-800';
      default: return 'bg-neutral-100 text-neutral-800';
    }
  };

  const getStatusLabel = (status: string) => t(`admin.ticketStatus.${status}`, { defaultValue: status });
  const getCategoryLabel = (category: string) => t(`admin.ticketCategory.${category}`, { defaultValue: category });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900">🎟️ {t('admin.supportTitle')}</h1>
        <p className="text-neutral-600 mt-2">{t('admin.supportSubtitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <button
          onClick={() => setStatusFilter('OPEN')}
          className={`p-4 rounded-lg border transition-all ${statusFilter === 'OPEN' ? 'border-red-500 bg-red-50 ring-2 ring-red-500/20' : 'border-neutral-200 bg-white hover:border-red-300'}`}
        >
          <p className="text-sm text-red-600 font-medium">🔴 {t('admin.open')}</p>
          <p className="text-2xl font-bold text-red-700 mt-1">{openCount}</p>
        </button>
        <button
          onClick={() => setStatusFilter('IN_PROGRESS')}
          className={`p-4 rounded-lg border transition-all ${statusFilter === 'IN_PROGRESS' ? 'border-yellow-500 bg-yellow-50 ring-2 ring-yellow-500/20' : 'border-neutral-200 bg-white hover:border-yellow-300'}`}
        >
          <p className="text-sm text-yellow-600 font-medium">🟡 {t('admin.inProgress')}</p>
          <p className="text-2xl font-bold text-yellow-700 mt-1">{inProgressCount}</p>
        </button>
        <button
          onClick={() => setStatusFilter('RESOLVED')}
          className={`p-4 rounded-lg border transition-all ${statusFilter === 'RESOLVED' ? 'border-green-500 bg-green-50 ring-2 ring-green-500/20' : 'border-neutral-200 bg-white hover:border-green-300'}`}
        >
          <p className="text-sm text-green-600 font-medium">🟢 {t('admin.resolved')}</p>
          <p className="text-2xl font-bold text-green-700 mt-1">{resolvedCount}</p>
        </button>
        <button
          onClick={() => setStatusFilter('all')}
          className={`p-4 rounded-lg border transition-all ${statusFilter === 'all' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20' : 'border-neutral-200 bg-white hover:border-blue-300'}`}
        >
          <p className="text-sm text-neutral-600 font-medium">📋 {t('admin.total')}</p>
          <p className="text-2xl font-bold text-neutral-900 mt-1">{total}</p>
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setStatusFilter('CLOSED')}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${statusFilter === 'CLOSED' ? 'bg-neutral-600 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
        >
          {t('admin.closed')} ({closedCount})
        </button>
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h2 className="font-semibold text-neutral-900">
            {statusFilter === 'all' ? t('admin.allTickets') : `${t('admin.tickets')} ${getStatusLabel(statusFilter).toLowerCase()}`}
          </h2>
          <span className="text-sm text-neutral-500">{t('admin.results', { count: filteredTickets.length })}</span>
        </div>

        {ticketsQuery.isLoading ? (
          <div className="p-4 sm:p-8 text-center text-neutral-500">{t('common.loading')}</div>
        ) : filteredTickets.length === 0 ? (
          <div className="p-4 sm:p-8 text-center text-neutral-500">{t('admin.noSupportTickets')}</div>
        ) : (
          <div className="divide-y divide-neutral-100">
            {filteredTickets.map((ticket: any) => (
              <div
                key={ticket.id}
                className="p-4 hover:bg-neutral-50 cursor-pointer transition-colors"
                onClick={() => setSelectedTicket(ticket)}
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm text-neutral-500">#{ticket.id.slice(0, 8)}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(ticket.status)}`}>
                        {getStatusLabel(ticket.status)}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getCategoryLabel(ticket.category)}
                      </span>
                    </div>
                    <p className="text-neutral-900 font-medium mt-2 line-clamp-2">
                      {ticket.messages?.[0]?.message || t('admin.noMessage')}
                    </p>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2 text-sm text-neutral-500">
                      <span>🏢 {ticket.establishment?.name || ticket.establishmentId?.slice(0, 8)}</span>
                      <span>👤 {ticket.createdBy?.email || t('admin.unknown')}</span>
                      <span>📅 {new Date(ticket.createdAt).toLocaleDateString(i18n.language, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>
                  <button className="text-neutral-400 hover:text-neutral-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {total > pageSize && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-neutral-600">
            {t('admin.showingPage', { from: page * pageSize + 1, to: Math.min((page + 1) * pageSize, total), total })}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
              className="w-full sm:w-auto px-4 py-2 rounded-lg border border-neutral-200 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed">
              {t('admin.previous')}
            </button>
            <button onClick={() => setPage(page + 1)} disabled={(page + 1) * pageSize >= total}
              className="w-full sm:w-auto px-4 py-2 rounded-lg border border-neutral-200 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed">
              {t('admin.next')}
            </button>
          </div>
        </div>
      )}

      {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 z-50 flex flex-col items-center justify-start sm:justify-center overflow-y-auto p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full my-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-bold text-lg text-neutral-900">{t('admin.ticket', { id: selectedTicket.id.slice(0, 8) })}</h3>
              <button onClick={() => setSelectedTicket(null)} className="p-2 hover:bg-neutral-100 rounded-lg transition-colors">
                <svg className="w-5 h-5 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">{t('admin.changeStatus')}</label>
                <div className="flex flex-wrap gap-2">
                  {(['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'] as const).map((status) => (
                    <button
                      key={status}
                      onClick={() => updateStatusMutation.mutate({ ticketId: selectedTicket.id, status })}
                      disabled={updateStatusMutation.isPending || selectedTicket.status === status}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 ${
                        selectedTicket.status === status ? `${getStatusStyle(status)} ring-2 ring-offset-2` : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                      }`}
                    >
                      {getStatusLabel(status)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">{t('admin.category')}</label>
                  <p className="text-neutral-900">{getCategoryLabel(selectedTicket.category)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">{t('admin.establishment')}</label>
                  <p className="text-neutral-900">{selectedTicket.establishment?.name || selectedTicket.establishmentId}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">{t('admin.createdBy')}</label>
                  <p className="text-neutral-900">{selectedTicket.createdBy?.email || t('admin.unknown')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">{t('admin.createdAt')}</label>
                  <p className="text-neutral-900">{new Date(selectedTicket.createdAt).toLocaleString(i18n.language)}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">{t('admin.messages')}</label>
                <div className="space-y-3">
                  {selectedTicket.messages?.length > 0 ? (
                    selectedTicket.messages.map((msg: any) => (
                      <div key={msg.id} className="bg-neutral-50 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-medium text-neutral-700">{msg.sender?.email || t('admin.unknown')}</span>
                          <span className="text-xs text-neutral-500">{new Date(msg.createdAt).toLocaleString(i18n.language)}</span>
                        </div>
                        <p className="text-neutral-700 whitespace-pre-wrap">{msg.message}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-neutral-500 text-sm">{t('admin.noMessages')}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
